import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { searchUsers, type UserSummary } from '../../../api/users';
import { createRoom } from '../../../api/chat';
import { useChatStore } from '../../../store/chatStore';
import { useAuthStore } from '../../../store/authStore';
import type { ChatRoomDto } from '../../../types/chat';
import { Check, ChevronLeft, Search, Users, X } from '../../../icons';
import { AiAvatar } from '../../../components/AiAvatar';

interface NewChatModalProps {
  onClose: () => void;
  /** 생성/재사용된 방 — ChatPage 가 rooms 목록에 반영 + 활성화. */
  onCreated: (room: ChatRoomDto) => void;
}

type Mode = 'choose' | 'ai' | 'people';

/**
 * NewChatModal — ChatList '새 채팅' 버튼이 여는 방 생성 모달.
 *
 *   - 진입: 2개 선택 카드 (AI 와 대화 / 동료와 대화)
 *   - AI: type='group' + memberIds=[] (백엔드가 본인만 멤버로 처리) → 생성 직후 aiMode ON
 *   - 동료: searchUsers 로 같은 기관 사용자 검색(250ms debounce) + 다중 선택
 *           1명 → direct, 2명+ → group(이름 필수)
 */
export function NewChatModal({ onClose, onCreated }: NewChatModalProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const [groupName, setGroupName] = useState('');
  const [aiRoomName, setAiRoomName] = useState('AI 와의 대화');
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleAiMode = useChatStore((s) => s.toggleAiMode);
  const setRoomMemberCount = useChatStore((s) => s.setRoomMemberCount);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // 사용자 검색 — debounce 250ms + AbortController. people mode 일 때만.
  useEffect(() => {
    if (mode !== 'people') return;
    const q = query.trim();
    if (q.length === 0) {
      setResults([]);
      setSearching(false);
      return;
    }
    const ctrl = new AbortController();
    setSearching(true);
    const id = window.setTimeout(() => {
      searchUsers(q, 10, ctrl.signal)
        .then((users) => setResults(users))
        .catch(() => { /* abort 또는 검색 실패 — 무시 */ })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      window.clearTimeout(id);
      ctrl.abort();
    };
  }, [query, mode]);

  // Esc 닫기.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (u: UserSummary) => {
    setSelected((prev) =>
      prev.some((s) => s.id === u.id) ? prev.filter((s) => s.id !== u.id) : [...prev, u],
    );
  };

  const isGroup = selected.length >= 2;
  const canCreatePeople =
    selected.length >= 1 && (!isGroup || groupName.trim().length > 0) && !creating;
  const canCreateAi = aiRoomName.trim().length > 0 && !creating;

  const handleCreatePeople = useCallback(async () => {
    if (selected.length === 0 || creating) return;
    setCreating(true);
    setError(null);
    try {
      const { room } = await createRoom({
        type: isGroup ? 'group' : 'direct',
        name: isGroup ? groupName.trim() : undefined,
        memberIds: selected.map((s) => s.id),
      });
      onCreated(room);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '채팅방 생성에 실패했습니다');
      setCreating(false);
    }
  }, [creating, groupName, isGroup, onCreated, selected]);

  const handleCreateAi = useCallback(async () => {
    if (creating || aiRoomName.trim().length === 0) return;
    if (!currentUserId) {
      setError('로그인 정보가 없어 AI 방을 생성할 수 없습니다');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      // backend schema 가 memberIds.minItems:1 강제 → 본인 id 를 보내고 backend 의
      // distinctMembers dedup (Set 으로 callerId 중복 제거) 으로 본인 1명 group 방 생성.
      // 생성 후 즉시 aiMode ON 토글 + roomMemberCount=1 (flash 회피).
      const { room } = await createRoom({
        type: 'group',
        name: aiRoomName.trim(),
        memberIds: [String(currentUserId)],
      });
      toggleAiMode(room.id);
      setRoomMemberCount(room.id, 1);
      onCreated(room);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'AI 채팅방 생성에 실패했습니다');
      setCreating(false);
    }
  }, [aiRoomName, creating, currentUserId, onCreated, setRoomMemberCount, toggleAiMode]);

  // chat-modal-portal (2026-07-02) — body 로 portal(채팅 트리 z-index:1 트랩 탈출 → 항상 최상단).
  return createPortal(
    <div className="chat-modal-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="chat-modal"
        role="dialog"
        aria-modal="true"
        aria-label="새 채팅"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="chat-modal-header">
          {mode !== 'choose' && (
            <button
              type="button"
              className="chat-modal-back"
              onClick={() => {
                setMode('choose');
                setError(null);
              }}
              aria-label="뒤로"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <h2 className="chat-modal-title">
            {mode === 'choose' ? '새 채팅' : mode === 'ai' ? 'AI 와 대화' : '동료와 대화'}
          </h2>
          <button type="button" className="chat-modal-close" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </header>

        {mode === 'choose' && (
          <div className="chat-modal-body">
            <div className="chat-modal-choice-cards" role="list">
              <button
                type="button"
                className="chat-modal-choice-card"
                onClick={() => setMode('ai')}
                role="listitem"
              >
                <span className="chat-modal-choice-icon chat-modal-choice-icon--ai" aria-hidden>
                  <AiAvatar variant="full" />
                </span>
                <span className="chat-modal-choice-meta">
                  <span className="chat-modal-choice-title">AI 와 대화</span>
                  <span className="chat-modal-choice-sub">혼자서 AI 어시스턴트와 새 대화를 시작합니다</span>
                </span>
              </button>
              <button
                type="button"
                className="chat-modal-choice-card"
                onClick={() => setMode('people')}
                role="listitem"
              >
                <span className="chat-modal-choice-icon chat-modal-choice-icon--people" aria-hidden>
                  <Users size={28} />
                </span>
                <span className="chat-modal-choice-meta">
                  <span className="chat-modal-choice-title">동료와 대화</span>
                  <span className="chat-modal-choice-sub">1명 선택 시 1:1, 2명 이상이면 그룹 채팅으로 자동 전환</span>
                </span>
              </button>
            </div>
          </div>
        )}

        {mode === 'ai' && (
          <>
            <div className="chat-modal-body">
              <div className="chat-modal-ai-hero" aria-hidden>
                <span className="chat-modal-ai-hero-icon">
                  <AiAvatar variant="full" />
                </span>
              </div>
              <div className="chat-modal-group-name">
                <label htmlFor="chat-new-ai-name">대화방 이름</label>
                <input
                  id="chat-new-ai-name"
                  type="text"
                  value={aiRoomName}
                  onChange={(e) => setAiRoomName(e.target.value)}
                  placeholder="AI 와의 대화"
                  maxLength={100}
                  autoFocus
                />
              </div>
              {error && <p className="chat-modal-error" role="alert">{error}</p>}
            </div>
            <footer className="chat-modal-footer">
              <button type="button" className="chat-modal-cancel" onClick={onClose}>취소</button>
              <button
                type="button"
                className="chat-modal-submit"
                onClick={() => void handleCreateAi()}
                disabled={!canCreateAi}
              >
                {creating ? '생성 중…' : 'AI 대화 시작'}
              </button>
            </footer>
          </>
        )}

        {mode === 'people' && (
          <>
            <div className="chat-modal-body">
              {selected.length > 0 && (
                <ul className="chat-modal-chips" aria-label="선택한 멤버">
                  {selected.map((u) => (
                    <li key={u.id} className="chat-modal-chip">
                      <span>{u.name}</span>
                      <button type="button" onClick={() => toggle(u)} aria-label={`${u.name} 제거`}>
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="chat-modal-search">
                <Search size={16} className="chat-modal-search-icon" />
                <input
                  type="search"
                  className="chat-modal-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="이름 또는 이메일로 검색"
                  aria-label="사용자 검색"
                  autoFocus
                />
              </div>

              <ul className="chat-modal-results" role="listbox" aria-label="검색 결과">
                {searching && <li className="chat-modal-hint">검색 중…</li>}
                {!searching && query.trim().length > 0 && results.length === 0 && (
                  <li className="chat-modal-hint">검색 결과가 없습니다</li>
                )}
                {results.map((u) => {
                  const on = selected.some((s) => s.id === u.id);
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        className={`chat-modal-result${on ? ' chat-modal-result--on' : ''}`}
                        onClick={() => toggle(u)}
                      >
                        <span className="chat-modal-result-avatar">{(u.name ?? '?').slice(0, 1)}</span>
                        <span className="chat-modal-result-meta">
                          <span className="chat-modal-result-name">{u.name}</span>
                          <span className="chat-modal-result-sub">{u.position ?? u.email}</span>
                        </span>
                        {on && <Check size={16} className="chat-modal-result-check" />}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {isGroup && (
                <div className="chat-modal-group-name">
                  <label htmlFor="chat-new-group-name">그룹 이름</label>
                  <input
                    id="chat-new-group-name"
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="그룹 채팅방 이름"
                    maxLength={100}
                  />
                </div>
              )}

              {error && <p className="chat-modal-error" role="alert">{error}</p>}
            </div>

            <footer className="chat-modal-footer">
              <button type="button" className="chat-modal-cancel" onClick={onClose}>취소</button>
              <button
                type="button"
                className="chat-modal-submit"
                onClick={() => void handleCreatePeople()}
                disabled={!canCreatePeople}
              >
                {creating ? '생성 중…' : isGroup ? '그룹 만들기' : '대화 시작'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
